import React, { useEffect, useState } from 'react';
import { configApi, SystemConfig as SystemConfigType } from '../../../api/config';
import { Card } from '../../../components/ui/Card';
import { Spinner } from '../../../components/ui/Spinner';
import { Badge } from '../../../components/ui/Badge';
import { IdentityProvider } from './IdentityProvider';

const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
      gap: 16,
      padding: '12px 20px',
      borderBottom: '1px solid var(--neutral-200)',
      alignItems: 'center',
    }}
  >
    <span style={{ fontSize: 13, color: 'var(--neutral-500)', fontWeight: 500 }}>{label}</span>
    <div style={{ fontSize: 13, color: 'var(--neutral-800)' }}>{children}</div>
  </div>
);

export const SystemConfig: React.FC = () => {
  const [config, setConfig] = useState<SystemConfigType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoading(true);
        const data = await configApi.getSystemConfig();
        setConfig(data);
      } catch {
        setError('Failed to load system configuration.');
      } finally {
        setLoading(false);
      }
    };
    void fetchConfig();
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner size={24} />
      </div>
    );
  }

  if (error || !config) {
    return (
      <div
        style={{
          padding: 16,
          background: 'var(--danger-100)',
          color: 'var(--danger-900)',
          fontSize: 13,
          borderRadius: 'var(--radius-md)',
        }}
      >
        {error}
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
    <IdentityProvider />
    <Card padding={0}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--neutral-300)' }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--neutral-800)' }}>
          System Configuration
        </h3>
        <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 2 }}>
          Read-only — managed via environment variables on Railway
        </p>
      </div>

      <InfoRow label="Banking Provider">
        <Badge variant="info">{config.bankingProvider}</Badge>
      </InfoRow>
      <InfoRow label="NGN Card Provider">
        <Badge variant="info">{config.cardNgnProvider}</Badge>
      </InfoRow>
      <InfoRow label="USD Card Provider">
        <Badge variant="info">{config.cardUsdProvider}</Badge>
      </InfoRow>
      <InfoRow label="AI Model">
        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{config.aiModel}</span>
      </InfoRow>
      <InfoRow label="AI Max Context Messages">{config.aiMaxContextMessages}</InfoRow>
      <InfoRow label="Max Login Attempts">{config.maxLoginAttempts}</InfoRow>
      <InfoRow label="Login Lockout Duration">{config.loginLockoutDurationMinutes} minutes</InfoRow>
    </Card>
    </div>
  );
};
